/**
 * SceneDepth — how far away the room is, measured in SLAM units.
 *
 * ## Why this exists
 *
 * Placement used to depend on metric scale, and scale is the least reliable
 * thing in the whole stack. When `metersPerUnit` is null — which is most of the
 * time, on most devices — `AlvaARBackend.anchor()` fell back to planting
 * fragments at a fixed 2.5 SLAM units (the value then; `ANCHOR_DEPTH_UNITS` is
 * 3.2 now). A SLAM unit is an arbitrary quantity that differs every session, so
 * that constant is a lottery.
 *
 * The capture of 2026-08-22 shows what it costs. Tracking was healthy by every
 * gate (98.6% uptime, zero resets, stepRatio 5.24, yaw within 1.2° of the gyro
 * across 74 s), yet nothing appeared attached to the room. The session's real
 * scale was ~0.2 m/unit, so the "2.5 metre" fallback planted text **half a metre
 * from the participant's face**, and at a median walking speed of 4.6 units/s the
 * camera crossed that entire distance in half a second. Fragments did not fail to
 * stick to the world; they stuck to a point inside the participant's personal
 * space and were overtaken instantly.
 *
 * ## The fix is to stop needing scale for this
 *
 * Where a fragment should sit is a question about the room, not about metres:
 * roughly as far away as the things in it. Triangulating tracked features against
 * the tracker's own poses answers that directly, in the tracker's own units —
 * the arbitrary scale cancels, because both sides of the comparison carry it.
 *
 * So this reports median scene depth in SLAM units, and `anchor()` places
 * fragments at a fraction of it. A unit can be a centimetre or a metre and the
 * fragment lands in the same physical place either way.
 *
 * Metric scale remains strictly better when it is genuinely known — it also
 * gives correct physical sizing and floor resting — so this is the middle rung of
 * a three-rung ladder, not a replacement:
 *
 *   1. metric scale locked      → place at a true `ANCHOR_DEPTH_M` (3.2 m)
 *   2. scene depth known        → place at a fraction of the room's depth  ← here
 *   3. nothing known            → the raw-unit constant, still a lottery
 *
 * This runs for the whole session, needs no network, and keeps measuring: the
 * room's depth changes as the participant walks from a corridor into a hall.
 * (A server-backed `DepthScale` used to triangulate alongside it to get metres;
 * it was removed on 2026-08-26 and nothing else consumes these triangulations.)
 */

import type { TrackedPoint } from './FeatureTracker.js';
import {
  triangulateOne,
  type Correspondence,
  type Quat,
  type TriangulationView,
  type Vec3,
} from './triangulate.js';

/** A point must have survived this many frames before it is trusted. */
const MIN_AGE = 3;
/** Matched features needed before an estimate is worth forming. */
const MIN_MATCHES = 8;
/**
 * Maximum ray gap, as a fraction of depth, before a triangulation is discarded.
 * (Inherited from the removed `scaleFromDepths`, so old captures stay comparable.)
 */
const MAX_RESIDUAL = 0.08;
/**
 * Baseline required, as a fraction of median scene depth.
 *
 * ⚠️ Was 0.04, on the argument that a noisy answer beats the constant it
 * replaces and that waiting longer leaves fragments wrong for longer. The field
 * captures of 2026-08-28 refuted it: 4% parallax does not produce a slightly
 * noisy depth, it produces a **meaningless** one. The iPhone session reported 62
 * different medians spanning 0.16 to 16.02 units — a 97× range — inside one
 * room, and anchors placed from those landed anywhere from the participant's
 * face to beyond the far wall.
 *
 * Depth error from triangulation goes roughly as 1/baseline, so this is the
 * single most effective lever on the noise. At the observed walking speeds
 * (0.6–1.6 units/s) 10% of a 5-unit room is reached in well under a second, so
 * the cost is small and the timeout below still catches a participant who
 * genuinely stands still.
 */
const MIN_BASELINE_FRACTION = 0.1;
/**
 * Re-anchor if no estimate forms in this long.
 *
 * A participant who stands still never accumulates baseline, and meanwhile the
 * anchor's features age out of view. Dropping it and starting fresh costs
 * nothing; holding a stale one means matching against pixels that have gone.
 */
const ANCHOR_TIMEOUT_MS = 4000;
/** Rolling window of accepted estimates; the reported depth is their median. */
const WINDOW = 15;
/**
 * Estimates required before reporting.
 *
 * ⚠️ Was 2, reasoning that each estimate is itself a median over ≥8 triangulated
 * points so two agreeing is a fair bar. But two values cannot *agree* about
 * anything — a two-sample median is just one of the two samples, and there is no
 * spread to test. In the 2026-08-28 Oppo capture the very first reported depth
 * (0.41 units, from exactly two estimates at 100 ms) placed eleven of thirteen
 * fragments about 4 cm from the lens, permanently, before the participant had
 * taken a step.
 *
 * Six is enough for the spread test below to mean something while still
 * arriving within a few seconds of walking. Anchors placed before it are
 * provisional and get corrected — that mechanism exists precisely so this can
 * afford to wait.
 */
const MIN_ESTIMATES = 6;
/**
 * Reject the reported depth while the interquartile spread exceeds this fraction
 * of the median.
 *
 * The same agreement test `GroundPlaneScale` applies, and for the same reason:
 * a median over disagreeing observations is not a measurement, it is a number.
 * Looser than that one's 0.35 because triangulating sparse features over a short
 * baseline is inherently noisier than fitting a plane, and because the
 * consequence of being somewhat wrong here is milder — text at a slightly wrong
 * distance rather than a scale factor every later anchor is multiplied by.
 */
const MAX_SPREAD = 0.6;

/**
 * How many triangulated world points to keep.
 *
 * The cloud is the room as the tracker has actually measured it, and it is the
 * only thing in the stack that knows one direction is nearer than another.
 * `triangulateOne` has always computed these points and they were always thrown
 * away — only the scalar depth escaped, so every placement question collapsed to
 * one median for the whole room, and text sat on a shell.
 *
 * Ring buffer, oldest evicted. A few hundred is plenty: the tracker yields at
 * most 48 points per frame and roughly 8-20 survive the residual filter per
 * accepted estimate, so this holds tens of seconds of exploration. Capping by
 * COUNT rather than by age is deliberate — a participant who stands still adds
 * nothing, so nothing ages out and the room they last looked at stays known.
 */
const CLOUD_MAX = 400;

/**
 * Half-angle of the cone searched around a ray, in degrees, and the widths tried
 * in order.
 *
 * Features are sparse, so a narrow cone usually finds nothing and a wide one is
 * indistinguishable from the global median. These widen until enough points are
 * found: a tight answer where the room is well observed, a looser one where it
 * is thin, and null rather than a fabricated one where it is unobserved.
 */
const CONE_DEG = [12, 22, 35];
/** Points needed inside the cone before its answer is worth anything. */
const CONE_MIN_POINTS = 4;
/**
 * Agreement bar for a cone's points, as interquartile spread over the median.
 *
 * Looser than the session-level MAX_SPREAD because a cone legitimately straddles
 * depth discontinuities — the edge of a table against the far wall is genuinely
 * two depths, not a bad measurement. Still tight enough to reject a cone whose
 * points are pure noise.
 */
const CONE_MAX_SPREAD = 0.9;

export interface SceneDepthStatus {
  /** Median scene depth in SLAM units, or null before enough estimates. */
  depth: number | null;
  /** Accepted estimates so far this session. */
  estimates: number;
  /** Points in the most recent accepted estimate. */
  lastUsed: number;
  /**
   * Interquartile spread of the current window as a fraction of its median —
   * the disagreement measure the report is gated on. Null before there is a
   * window to measure. Shown in the HUD so a session that never settles is
   * distinguishable from one that never measures.
   */
  spread: number | null;
  /** True once a depth has been reported and is being held. */
  settled: boolean;
  /** Triangulated world points currently held — the measured room. */
  cloud: number;
}

interface Anchor {
  at: number;
  position: Vec3;
  orientation: Quat;
  points: Map<number, { x: number; y: number }>;
}

export class SceneDepth {
  private anchor: Anchor | null = null;
  private window: number[] = [];
  private estimates = 0;
  private lastUsed = 0;
  /**
   * The last depth the window actually agreed on.
   *
   * Held rather than recomputed per call, so that a window which temporarily
   * falls into disagreement does not withdraw the depth. Withdrawing it would
   * drop placement to the lottery constant mid-session — which is exactly the
   * 12× swing observed on the Oppo when metric scale did the same thing (see
   * STATUS §8.10). A stale but agreed measurement is strictly better than a
   * constant, and unlike the constant it is at least about this room.
   */
  private held: number | null = null;
  /** The measured room: triangulated points in world space. Ring buffer. */
  private cloud: Vec3[] = [];
  private cloudNext = 0;

  /**
   * Forget the reference frame, keeping everything already measured.
   *
   * For a tracking dropout: the feature ids it holds will not survive (the
   * tracker re-detects after a gap) and its pose is from before the gap, so
   * pairing it with anything afterwards would triangulate across a
   * relocalisation. Unlike `reset()` the map — and so the units — is the same,
   * so the window, the held depth and the cloud stay valid.
   */
  dropAnchor(): void {
    this.anchor = null;
  }

  reset(): void {
    this.anchor = null;
    this.window = [];
    this.estimates = 0;
    this.lastUsed = 0;
    this.held = null;
    // The cloud is in the OLD map's frame and units. Keeping it across a reset
    // would place text against a coordinate system that no longer exists.
    this.cloud = [];
    this.cloudNext = 0;
  }

  /**
   * Interquartile spread of the window as a fraction of its median, or null
   * while there is nothing to measure. Lower is more agreement.
   */
  private spread(): number | null {
    const n = this.window.length;
    if (n < MIN_ESTIMATES) return null;
    const s = [...this.window].sort((a, b) => a - b);
    const med = s[n >> 1]!;
    if (!(med > 0)) return null;
    return (s[Math.floor(n * 0.75)]! - s[Math.floor(n * 0.25)]!) / med;
  }

  /**
   * Median scene depth in SLAM units, or null while still unknown.
   *
   * The median of a rolling window rather than the newest value: a single
   * estimate taken while the participant faced a wall is not wrong so much as
   * unrepresentative, and anchors placed from it would cluster in the wrong
   * place permanently.
   *
   * ⚠️ Gated on the window AGREEING, not merely on it being full. Without that
   * gate this returned a median over values spanning two orders of magnitude
   * and every caller treated it as a measurement — see MIN_ESTIMATES. Once a
   * value has been agreed it is held through later disagreement and replaced
   * only by a fresh agreement, so the reported depth moves in steps between
   * things actually measured rather than tracking the noise.
   */
  depth(): number | null {
    const sp = this.spread();
    if (sp !== null && sp <= MAX_SPREAD) {
      const s = [...this.window].sort((a, b) => a - b);
      this.held = s[s.length >> 1] ?? this.held;
    }
    return this.held;
  }

  status(): SceneDepthStatus {
    const depth = this.depth();
    return {
      depth,
      estimates: this.estimates,
      lastUsed: this.lastUsed,
      spread: this.spread(),
      settled: depth !== null,
      cloud: this.cloud.length,
    };
  }

  /**
   * How far away the room is ALONG ONE RAY, in SLAM units — or null when
   * nothing has been measured in that direction.
   *
   * This is the question `depth()` cannot answer. A single session-wide median
   * says the room is, on average, so deep; it cannot say that the wall ahead is
   * near and the corridor to the left is far. Text placed from the median sits
   * on a shell at one distance regardless of what is actually there.
   *
   * Searches a widening cone about the ray and reports the median distance of
   * the points inside it, projected onto the ray. Widening rather than fixed
   * because features are sparse: a narrow cone finds nothing in a thinly
   * observed direction, a wide one is just the global median wearing a hat.
   *
   * ⚠️ **Returns null rather than guessing.** A direction the camera has never
   * looked at has no measured depth, and inventing one is precisely the habit
   * that produced the shell. Callers fall back to `depth()` explicitly, so the
   * fallback is visible in the code and in telemetry rather than hidden here.
   * At session start this returns null for most bearings, and correctly so —
   * the camera has seen ~±25° of a ±70° anchor spread.
   *
   * @param origin Ray origin in world space (the camera position it was cast from).
   * @param dir Unit direction in world space.
   */
  depthAlongRay(origin: Vec3, dir: Vec3): number | null {
    if (this.cloud.length < CONE_MIN_POINTS) return null;
    const dl = Math.hypot(dir.x, dir.y, dir.z);
    if (!(dl > 0)) return null;
    const nx = dir.x / dl;
    const ny = dir.y / dl;
    const nz = dir.z / dl;

    for (const deg of CONE_DEG) {
      const minCos = Math.cos((deg * Math.PI) / 180);
      const along: number[] = [];
      for (const p of this.cloud) {
        const rx = p.x - origin.x;
        const ry = p.y - origin.y;
        const rz = p.z - origin.z;
        // Distance along the ray. Negative means behind the origin.
        const t = rx * nx + ry * ny + rz * nz;
        if (!(t > 0)) continue;
        const r = Math.hypot(rx, ry, rz);
        if (!(r > 0)) continue;
        // cos of the angle between the ray and this point.
        if (t / r < minCos) continue;
        along.push(t);
      }
      if (along.length < CONE_MIN_POINTS) continue;

      along.sort((a, b) => a - b);
      const n = along.length;
      const med = along[n >> 1]!;
      if (!(med > 0) || !Number.isFinite(med)) continue;
      // Same agreement discipline as everywhere else in this file: a median
      // over points that disagree is not a measurement.
      const spread = (along[Math.floor(n * 0.75)]! - along[Math.floor(n * 0.25)]!) / med;
      if (spread > CONE_MAX_SPREAD) continue;
      return med;
    }
    return null;
  }

  /**
   * Offer the current tracking step. Cheap: does nothing until a baseline has
   * accumulated, and holds only feature ids and two poses in between.
   */
  offer(
    now: number,
    position: Vec3,
    orientation: Quat,
    points: TrackedPoint[],
    tanHalfX: number,
    tanHalfY: number,
  ): void {
    const usable = points.filter((p) => p.age >= MIN_AGE);
    if (usable.length < MIN_MATCHES) return;

    if (!this.anchor) {
      this.setAnchor(now, position, orientation, usable);
      return;
    }

    const a = this.anchor;
    if (now - a.at > ANCHOR_TIMEOUT_MS) {
      this.setAnchor(now, position, orientation, usable);
      return;
    }

    const pairs: Correspondence[] = [];
    for (const p of usable) {
      const before = a.points.get(p.id);
      if (before) pairs.push({ u1: before.x, v1: before.y, u2: p.x, v2: p.y });
    }
    if (pairs.length < MIN_MATCHES) return;

    const viewA: TriangulationView = { position: a.position, orientation: a.orientation };
    const viewB: TriangulationView = { position, orientation };

    const depths: number[] = [];
    const fresh: Vec3[] = [];
    for (const c of pairs) {
      const t = triangulateOne(c, viewA, viewB, tanHalfX, tanHalfY);
      if (t && t.residual <= MAX_RESIDUAL) {
        depths.push(t.depth);
        fresh.push(t.world);
      }
    }
    if (depths.length < MIN_MATCHES) return;

    depths.sort((x, y) => x - y);
    const median = depths[depths.length >> 1]!;
    if (!(median > 0) || !Number.isFinite(median)) return;

    // Near-parallel rays give confidently wrong depths. Judging the baseline
    // AFTER triangulating is deliberate: "enough parallax" is only meaningful
    // relative to how far away the scene turned out to be.
    const baseline = Math.hypot(
      position.x - a.position.x,
      position.y - a.position.y,
      position.z - a.position.z,
    );
    if (baseline < median * MIN_BASELINE_FRACTION) return;

    this.window.push(median);
    if (this.window.length > WINDOW) this.window.shift();
    this.estimates++;
    this.lastUsed = depths.length;

    // Keep the points, not just their median. Added AFTER the baseline test, so
    // the cloud inherits every quality gate the scalar estimate passes — points
    // from an under-parallaxed frame pair never enter it at all.
    for (const w of fresh) {
      if (this.cloud.length < CLOUD_MAX) this.cloud.push(w);
      else {
        this.cloud[this.cloudNext] = w;
        this.cloudNext = (this.cloudNext + 1) % CLOUD_MAX;
      }
    }

    // Re-anchor here so the next estimate measures a fresh stretch of the room
    // rather than re-deriving the same one from an ever-older frame.
    this.setAnchor(now, position, orientation, usable);
  }

  private setAnchor(
    now: number,
    position: Vec3,
    orientation: Quat,
    usable: TrackedPoint[],
  ): void {
    this.anchor = {
      at: now,
      position: { ...position },
      orientation: { ...orientation },
      points: new Map(usable.map((p) => [p.id, { x: p.x, y: p.y }])),
    };
  }
}
